import type { Intake } from "./intake"
import { STEPS, stepItems, type Item } from "./steps"
import { addLog, toggleCheck, type Workspace } from "./workspace"

/**
 * "Do these now": the handful of things that matter in the first hours, shown at the top of the plan.
 * Each one is backed by a real checklist item, so ticking it here ticks it in its step (and the other
 * way round), and the time is added to the Timeline. Which ones appear depends on what happened.
 */
export interface NowAction {
  id: string
  /** Checklist items that mean the same thing for different situations; the first one that applies is used. */
  itemIds: string[]
  /** What to do. */
  label: string
  /** Logged to the Timeline when ticked. */
  doneText: string
  detail?: string
  link?: { href: string; label: string }
  /** The step that explains it. */
  step: string
  /** Incident types this should not appear for even though its checklist item applies. */
  hideFor?: string[]
}

export const NOW_ACTIONS: NowAction[] = [
  { id: "lead", itemIds: ["start-lead"], label: "Name one person in charge", doneText: "Named a person in charge", detail: "Everyone else reports to them.", step: "start" },
  { id: "isolate", itemIds: ["con-isolate"], label: "Disconnect affected computers from the network", doneText: "Disconnected affected computers", detail: "Don't wipe them or power them off.", step: "contain" },
  { id: "accounts", itemIds: ["con-accounts"], label: "Reset passwords and sign out sessions on affected accounts", doneText: "Reset passwords and signed out sessions", detail: "Use a clean device.", step: "contain" },
  { id: "locate", itemIds: ["con-wipe"], label: "Lock or locate the device; wipe it if it's gone for good", doneText: "Remotely wiped the device", detail: "Find My Device or Find My first. Note the time. If you can't wipe it, assign it.", link: { href: "/guides/lost-device", label: "How to wipe it" }, step: "contain" },
  { id: "maker", itemIds: ["con-report-maker"], label: "Report it stolen to the maker (Apple, Dell, and so on)", doneText: "Reported the device to the maker", detail: "Give them the serial number so they can flag it if someone brings it in for service.", step: "contain" },
  { id: "police", itemIds: ["con-police"], label: "File a police report", doneText: "Filed a police report", detail: "Keep the report number.", step: "contain" },
  { id: "bank", itemIds: ["con-recipient-bank"], label: "Ask your bank to freeze and recall the payment", doneText: "Asked the bank to freeze and recall the payment", detail: "Recalls work bank to bank, and speed matters.", step: "contain" },
  { id: "demand", itemIds: ["con-demand-record"], label: "Save the demand exactly as you received it", doneText: "Saved the demand", detail: "Don't reply or pay before talking to your lawyer and law enforcement.", step: "contain" },
  { id: "paid", itemIds: ["con-paid"], label: "Write down exactly what was paid, when, and to whom", doneText: "Recorded the payment", step: "contain" },
  { id: "lawyer", itemIds: ["start-counsel", "start-counsel-have"], label: "Talk to your lawyer", doneText: "Talked to a lawyer", detail: "Before you contact regulators or customers.", link: { href: "/find-a-lawyer", label: "Find a breach lawyer" }, step: "start" },
  { id: "insurer", itemIds: ["start-insurer", "start-insurer-have"], label: "Call your cyber insurer", doneText: "Called the cyber insurer", detail: "Ask for a claim number and their approved firms.", link: { href: "/insurance", label: "Find your insurer's claims contact" }, step: "start" },
  { id: "ir", itemIds: ["start-ir-have", "start-ir"], label: "Get an incident-response firm on the phone", doneText: "Contacted an incident-response firm", link: { href: "/directory/ir-guidance", label: "How to choose one" }, step: "start" },
  { id: "leo", itemIds: ["not-leo"], label: "Report to the FBI", doneText: "Reported to law enforcement (FBI)", detail: "ic3.gov or your local field office.", link: { href: "/directory/report#fbi-ic3", label: "Where to report" }, step: "notify", hideFor: ["lost-device"] },
]

export interface ResolvedNow { action: NowAction; item: Item; done: boolean; doneAt?: string; owner?: string }

/** The actions that apply to this incident, in order, with their live state. */
export function nowFor(w: Workspace): ResolvedNow[] {
  const applicable = new Map<string, Item>()
  for (const s of STEPS) for (const it of stepItems(s, w.intake)) applicable.set(it.id, it)
  const out: ResolvedNow[] = []
  for (const action of NOW_ACTIONS) {
    if (action.hideFor && w.intake.incidentType && action.hideFor.includes(w.intake.incidentType)) continue
    const id = action.itemIds.find((x) => applicable.has(x))
    if (!id) continue
    const c = w.checks[id]
    out.push({ action, item: applicable.get(id) as Item, done: !!c?.done, doneAt: c?.doneAt, owner: c?.owner })
  }
  return out
}

export function nowProgress(w: Workspace): { done: number; total: number } {
  const list = nowFor(w)
  return { done: list.filter((x) => x.done).length, total: list.length }
}

/** Tick or untick one of the actions: updates the shared checklist item and logs the time when ticked. */
export function setNowDone(w: Workspace, r: ResolvedNow, done: boolean, now = new Date()): Workspace {
  const next = toggleCheck(w, r.item.id, done, now)
  return done ? addLog(next, `Done: ${r.action.doneText}.`, now) : next
}

export type { Intake }
